import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { FlowchartFrame, VerticalChain, CourseNode, useCourseModal } from '../components/FlowchartParts';
import { CourseModal } from '../components/CourseModal';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { ChevronRight, Sparkles, Compass, Award, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleTrigger } from '../components/ui/collapsible';
import { useSettings } from '../context/SettingsContext';

const CORE_ORDER = ['open-water-scuba-diver', 'advanced-diver', 'rescue-diver', 'master-scuba-diver'];
const PRO_ORDER = ['sdi-divemaster', 'sdi-assistant-instructor', 'sdi-instructor', 'sdi-elite-scuba-instructor', 'sdi-course-director', 'sdi-instructor-trainer'];

export default function SDICourses() {
  const { settings } = useSettings();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const modal = useCourseModal();
  const [showAllSpecialties, setShowAllSpecialties] = useState(false);

  useEffect(() => {
    api.get('/courses?agency=SDI').then(({ data }) => setAll(data || [])).catch(() => setAll([])).finally(() => setLoading(false));
  }, []);

  const bySlug = useMemo(() => new Map(all.map((c) => [c.slug, c])), [all]);
  const core = CORE_ORDER.map((s) => bySlug.get(s)).filter(Boolean);
  const pro = PRO_ORDER.map((s) => bySlug.get(s)).filter(Boolean);
  const entry = all.filter((c) => c.category === 'entry').sort((a, b) => a.order - b.order);
  const specialties = all.filter((c) => c.category === 'specialty').sort((a, b) => a.order - b.order);
  const nonDivingInstructor = bySlug.get('sdi-non-diving-specialty-instructor');
  const bg = settings?.sdi_background_url || '/backgrounds/sdi-bg.jpg';

  return (
    <div className="pt-24 pb-20">
      <section className="relative overflow-hidden bg-[#0a1628]">
        {/* Hero: SDI/TDI/ERDI/PFI agency banner */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <img
            src={bg}
            alt=""
            className="absolute inset-y-0 right-0 h-full w-full sm:w-3/5 lg:w-1/2 object-contain object-right opacity-95"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628] via-[#0a1628]/92 to-[#0a1628]/35" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a1628]" />
          <div className="absolute inset-0 hero-radial" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24 relative">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#00b4d8] font-semibold mb-3">
            <Link to="/" className="hover:text-white">Home</Link> <ChevronRight className="inline h-3 w-3 mx-1" /> Courses <ChevronRight className="inline h-3 w-3 mx-1" /> SDI
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl">SDI Recreational Diver Pathway</h1>
          <p className="mt-5 max-w-2xl text-white/75 text-base sm:text-lg leading-relaxed">
            Whether you’re trying scuba for the first time, working towards your first certification, adding to your diving abilities, or becoming a dive professional — SDI has the course that’s right for you. Click any node below to view the details.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Badge className="bg-white/6 text-white/75 border border-white/12 hover:bg-white/8"><Compass className="mr-1 h-3 w-3 text-[#00b4d8]" /> Core path</Badge>
            <Badge className="bg-white/6 text-white/75 border border-white/12 hover:bg-white/8"><Sparkles className="mr-1 h-3 w-3 text-[#c8e600]" /> Specialty</Badge>
            <Badge className="bg-white/6 text-white/75 border border-white/12 hover:bg-white/8"><Award className="mr-1 h-3 w-3 text-[#c8e600]" /> Professional</Badge>
          </div>
        </div>
      </section>

      <section className="pb-4 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid lg:grid-cols-12 gap-6">
              <div className="h-96 rounded-3xl bg-white/5 border border-white/10 animate-pulse lg:col-span-3" />
              <div className="h-96 rounded-3xl bg-white/5 border border-white/10 animate-pulse lg:col-span-5" />
              <div className="h-96 rounded-3xl bg-white/5 border border-white/10 animate-pulse lg:col-span-4" />
            </div>
          ) : (
            <div data-testid="sdi-flowchart" className="grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-3">
                <FlowchartFrame title="Entry Level" subtitle="Get a feel for diving before committing">
                  <div className="space-y-3">
                    {entry.map((c) => <CourseNode key={c.slug} course={c} onClick={modal.openCourse} />)}
                  </div>
                </FlowchartFrame>
              </div>

              <div className="lg:col-span-5">
                <FlowchartFrame title="Core Pathway → Professional" subtitle="The main certification ladder">
                  <VerticalChain courses={core} onClick={modal.openCourse} variant="core" />
                  <div className="my-3 flex justify-center">
                    <div className="h-6 w-px bg-gradient-to-b from-[#00b4d8]/80 to-[#c8e600]/60" />
                  </div>
                  <div className="mb-3 text-[11px] uppercase tracking-[0.22em] text-[#c8e600] font-semibold text-center">Go Pro</div>
                  <VerticalChain courses={pro} onClick={modal.openCourse} variant="professional" />
                  {nonDivingInstructor && (
                    <div className="mt-5 pt-5 border-t border-dashed border-white/12">
                      <CourseNode course={nonDivingInstructor} onClick={modal.openCourse} variant="professional" badge="Optional" />
                    </div>
                  )}
                </FlowchartFrame>
              </div>

              <div className="lg:col-span-4">
                <FlowchartFrame title="Specialty Courses" subtitle="Add depth and breadth to your diving">
                  <div className="lg:hidden">
                    <Collapsible open={showAllSpecialties} onOpenChange={setShowAllSpecialties}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {(showAllSpecialties ? specialties : specialties.slice(0, 6)).map((c) => (
                          <CourseNode key={c.slug} course={c} onClick={modal.openCourse} />
                        ))}
                      </div>
                      <CollapsibleTrigger className="mt-4 inline-flex items-center gap-1 text-sm text-[#00b4d8] hover:text-white">
                        {showAllSpecialties ? 'Show less' : `Show ${specialties.length - 6} more`} <ChevronDown className={`h-4 w-4 transition-transform ${showAllSpecialties ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                    </Collapsible>
                  </div>
                  <ScrollArea className="hidden lg:block h-[820px] pr-3 scrollbar-thin">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                      {specialties.map((c) => (
                        <CourseNode key={c.slug} course={c} onClick={modal.openCourse} />
                      ))}
                    </div>
                  </ScrollArea>
                </FlowchartFrame>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/65">Not sure where to start? <Link to="/about" className="text-[#00b4d8] hover:text-white">Get in touch</Link> and we’ll match you to the right pathway.</p>
        </div>
      </section>

      <CourseModal open={modal.open} onOpenChange={modal.setOpen} course={modal.course} />
    </div>
  );
}
